/** PRO-05 Panier · PRO-06 Passage de commande (remise → paiement → récapitulatif). */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Choice, EmptyState, QuantityInput, SelectField, Steps, Table, type Etape } from '@myron/design-system';
import { ShoppingCart, Trash2 } from 'lucide-react';
import type { S } from '@myron/contracts';
import { ApiErreur, get, messageErreur, nouvelleCleIdempotence, post } from '../../shared/api/client';
import { DecisionOuverte, EntetePage, ErreurFormulaire, EtatRequete, MentionSimule } from '../../shared/ui/composants';
import { dateCourte, dateHeure, euros, LIBELLE_MOYEN } from '../../shared/ui/format';
import { cles, LIBELLE_ALERTE, LIBELLE_SOURCE, useEntrepots, useMutationsPanier, usePanier, type Choix, type Commande, type Devis, type LignePanier, type Panier as TPanier } from './api';

const LIB_COND: Record<string, string> = { PALETTE: 'Palette', CARTON: 'Carton', PACK: 'Pack' };

export function Panier() {
  const q = usePanier();
  const { modifier, supprimer, vider } = useMutationsPanier();
  const nav = useNavigate();
  const erreur = modifier.error ?? supprimer.error ?? vider.error;
  return (
    <>
      <EntetePage titre="Panier" sousTitre="Prix recalculés à chaque modification, hors taxes." />
      <EtatRequete
        requete={q}
        vide={(p) => p.lignes.length === 0}
        titreVide="Votre panier est vide"
        texteVide="Ajoutez des produits depuis le catalogue ou la commande rapide."
        actionVide={<div className="my-ligne"><Link className="my-btn my-btn--primaire" to="/pro/catalogue">Parcourir le catalogue</Link><Link className="my-btn my-btn--secondaire" to="/pro/commande-rapide">Commande rapide</Link></div>}
        enfants={(p: TPanier) => {
          const alertes = p.lignes.filter((l) => l.alerte);
          const minimum = p.minimumCommandeHtCentimes ?? 0;
          const manque = Math.max(0, minimum - (p.totalHtCentimes ?? 0));
          const franco = p.francoHtCentimes ?? 0;
          return (
            <div className="my-grille-2">
              <div className="my-pile">
                <ErreurFormulaire erreur={erreur} />
                {alertes.length > 0 && <Alert ton="alerte" titre="Lignes à corriger">Certaines lignes ne respectent pas les règles de vente : corrigez-les avant de commander.</Alert>}
                <Table<LignePanier>
                  legende="Lignes du panier"
                  lignes={p.lignes}
                  cle={(l) => l.id}
                  colonnes={[
                    { cle: 'p', titre: 'Produit', rendu: (l) => <span className="my-pile my-pile--serre" style={{ gap: 2 }}><strong>{l.libelle}</strong><span className="my-legende my-secondaire">Réf. {l.sku} · {LIB_COND[l.conditionnementType!]}</span>{l.alerte && <Badge ton="alerte">{LIBELLE_ALERTE[l.alerte]}</Badge>}</span> },
                    { cle: 'pu', titre: 'Prix unitaire HT', num: true, rendu: (l) => <span className="my-pile my-pile--serre" style={{ gap: 0 }}><span>{euros(l.prixUnitaireHtCentimes)}</span>{l.prixBarreCentimes ? <span className="my-prix-barre my-legende">{euros(l.prixBarreCentimes)}</span> : null}<span className="my-legende my-secondaire">{LIBELLE_SOURCE[l.source!]}</span></span> },
                    { cle: 'q', titre: 'Quantité', rendu: (l) => <QuantityInput label={`Quantité ${l.libelle}`} valeur={l.quantite!} min={1} pas={l.multiple || 1} onChange={(v) => modifier.mutate({ id: l.id, quantite: v })} /> },
                    { cle: 't', titre: 'Total HT', num: true, rendu: (l) => <strong>{euros(l.totalHtCentimes)}</strong> },
                    { cle: 'a', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (l) => <Button variante="tertiaire" compact icone={Trash2} onClick={() => supprimer.mutate(l.id)} aria-label={`Retirer ${l.libelle}`}>Retirer</Button> },
                  ]}
                />
                <div><Button variante="tertiaire" onClick={() => vider.mutate()} chargement={vider.isPending}>Vider le panier</Button></div>
              </div>
              <Card as="section" aria-labelledby="titre-total">
                <div className="my-pile">
                  <h2 id="titre-total" className="my-titre-4">Total</h2>
                  <dl className="my-dl">
                    <dt>Total HT</dt><dd data-testid="total-panier">{euros(p.totalHtCentimes)}</dd>
                    <dt>Minimum de commande</dt><dd>{euros(minimum)} HT</dd>
                    <dt>Franco de port</dt><dd>{euros(franco)} HT</dd>
                  </dl>
                  {manque > 0
                    ? <Alert ton="alerte" titre="Minimum non atteint">Ajoutez {euros(manque)} HT pour pouvoir commander.</Alert>
                    : (p.totalHtCentimes ?? 0) >= franco ? <Alert ton="succes">Livraison offerte (franco atteint).</Alert> : <p className="my-petit my-secondaire">Encore {euros(franco - (p.totalHtCentimes ?? 0))} HT pour la livraison offerte.</p>}
                  <p className="my-legende my-secondaire">TVA et frais de livraison calculés à l’étape suivante. Le stock est réservé à la confirmation du paiement.</p>
                  <Button icone={ShoppingCart} disabled={alertes.length > 0 || manque > 0} onClick={() => nav('/pro/commande')}>Passer commande</Button>
                </div>
              </Card>
            </div>
          );
        }}
      />
    </>
  );
}

type Etapes = 'remise' | 'paiement' | 'recap';

export function Checkout() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const panier = usePanier();
  const entrepots = useEntrepots();
  const adresses = useQuery({ queryKey: ['pro', 'adresses'], queryFn: () => get<S<'Adresse'>[]>('/pro/entreprise/adresses'), retry: false });
  const [etape, setEtape] = useState<Etapes>('remise');
  const [mode, setMode] = useState<'LIVRAISON' | 'RETRAIT'>('LIVRAISON');
  const [entrepotId, setEntrepotId] = useState('');
  const [adresseId, setAdresseId] = useState('');
  const [creneau, setCreneau] = useState('');
  const [moyen, setMoyen] = useState<Choix['moyenPaiement'] | ''>('');
  const [erreur, setErreur] = useState<unknown>(null);
  const [nouveauTotal, setNouveauTotal] = useState<number | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const cle = useRef<string | null>(null);

  const candidats = (entrepots.data ?? []).filter((e) => (mode === 'RETRAIT' ? e.retraitActif : e.livraisonActive));
  useEffect(() => { if (candidats.length && !candidats.some((c) => c.id === entrepotId)) setEntrepotId(candidats[0]!.id!); }, [candidats, entrepotId]);
  const livraisons = (adresses.data ?? []).filter((a) => a.type === 'LIVRAISON');
  useEffect(() => { if (!adresseId && livraisons.length) setAdresseId((livraisons.find((a) => a.parDefaut) ?? livraisons[0])!.id!); }, [livraisons, adresseId]);

  const creneaux = useQuery({
    queryKey: ['pro', 'creneaux', entrepotId],
    queryFn: () => get<S<'CreneauDisponible'>[]>('/pro/creneaux-retrait', { entrepotId }),
    enabled: mode === 'RETRAIT' && !!entrepotId,
  });
  const [creneauId, dateRetrait] = creneau.split('|');
  const choixBase = useMemo(() => ({
    modeRemise: mode,
    entrepotId,
    ...(mode === 'LIVRAISON' && adresseId ? { adresseLivraisonId: adresseId } : {}),
    ...(mode === 'RETRAIT' && creneauId ? { creneauRetraitId: creneauId, dateRetrait } : {}),
  }), [mode, entrepotId, adresseId, creneauId, dateRetrait]);
  const remiseOk = !!entrepotId && (mode === 'LIVRAISON' || !!creneau);
  const moyensCandidats: Choix['moyenPaiement'][] = mode === 'RETRAIT' ? ['CB_EN_LIGNE', 'VIREMENT', 'CB_RETRAIT'] : ['CB_EN_LIGNE', 'VIREMENT'];

  // Les moyens autorisés sont ceux pour lesquels le serveur accepte d'établir un devis.
  const sondes = useQueries({
    queries: moyensCandidats.map((m) => ({
      queryKey: ['pro', 'devis', choixBase, m],
      queryFn: () => post<Devis>('/pro/checkout/devis', { ...choixBase, moyenPaiement: m }),
      enabled: etape !== 'remise' && remiseOk,
      retry: false,
    })),
  });
  const devisCourant = moyen ? sondes[moyensCandidats.indexOf(moyen)] : undefined;
  const devis = devisCourant?.data;
  useEffect(() => { cle.current = null; setNouveauTotal(null); }, [choixBase, moyen]);

  const soumettre = async () => {
    if (!devis || !moyen) return;
    setEnvoi(true);
    setErreur(null);
    cle.current ??= nouvelleCleIdempotence();
    try {
      const c = await post<Commande>('/pro/commandes', { choix: { ...choixBase, moyenPaiement: moyen }, empreintePrix: devis.empreintePrix }, { idempotence: cle.current });
      await qc.invalidateQueries({ queryKey: cles.panier });
      void qc.invalidateQueries({ queryKey: ['pro', 'commandes'] });
      if (moyen === 'CB_EN_LIGNE') {
        const p = await post<S<'InitiationPaiement'>>(`/pro/commandes/${c.id}/paiement`, undefined, { idempotence: nouvelleCleIdempotence() });
        nav(p.urlRedirection ?? `/pro/commandes/${c.id}`);
      } else nav(`/pro/commandes/${c.id}?nouvelle=1`);
    } catch (e) {
      if (e instanceof ApiErreur && e.code === 'PRIX_OBSOLETE') {
        setNouveauTotal(Number(e.probleme?.nouveauTotalTtcCentimes ?? 0));
        cle.current = null;
        await devisCourant?.refetch();
      }
      setErreur(e);
    } finally { setEnvoi(false); }
  };

  const etapes: Etape[] = [
    { titre: 'Remise', detail: mode === 'LIVRAISON' ? 'Livraison' : 'Retrait', etat: etape === 'remise' ? 'encours' : 'fait' },
    { titre: 'Paiement', detail: moyen ? LIBELLE_MOYEN[moyen] : undefined, etat: etape === 'paiement' ? 'encours' : etape === 'recap' ? 'fait' : 'avenir' },
    { titre: 'Récapitulatif', etat: etape === 'recap' ? 'encours' : 'avenir' },
  ];

  if (panier.data && panier.data.lignes.length === 0 && !envoi) {
    return <><EntetePage titre="Commande" /><EmptyState icone={ShoppingCart} titre="Votre panier est vide" action={<Link className="my-btn my-btn--primaire" to="/pro/catalogue">Parcourir le catalogue</Link>} /></>;
  }

  return (
    <>
      <EntetePage titre="Passer commande" fil={[{ libelle: 'Panier', vers: '/pro/panier' }, { libelle: 'Commande' }]} />
      <Steps etapes={etapes} label="Étapes de la commande" />
      <div style={{ marginTop: 24 }} className="my-pile">
        {etape === 'remise' && (
          <Card as="section" aria-labelledby="t-remise">
            <div className="my-pile">
              <h2 id="t-remise" className="my-titre-4">Mode de remise</h2>
              <fieldset className="my-pile my-pile--serre" style={{ border: 0, padding: 0, margin: 0 }}>
                <legend className="my-sr">Mode de remise</legend>
                <Choice type="radio" name="mode" label="Livraison à votre établissement" checked={mode === 'LIVRAISON'} onChange={() => { setMode('LIVRAISON'); setMoyen(''); }} />
                <Choice type="radio" name="mode" label="Retrait en entrepôt sur créneau" checked={mode === 'RETRAIT'} onChange={() => { setMode('RETRAIT'); setMoyen(''); }} />
              </fieldset>
              <SelectField label={mode === 'LIVRAISON' ? 'Entrepôt expéditeur' : 'Entrepôt de retrait'} value={entrepotId} onChange={(e) => { setEntrepotId(e.target.value); setCreneau(''); }} options={candidats.map((e) => ({ value: e.id!, label: `${e.nom} (${e.ville})` }))} />
              {mode === 'LIVRAISON' && (
                livraisons.length
                  ? <SelectField label="Adresse de livraison" value={adresseId} onChange={(e) => setAdresseId(e.target.value)} options={livraisons.map((a) => ({ value: a.id!, label: `${a.libelle ?? 'Adresse'} — ${a.ligne1}, ${a.codePostal} ${a.ville}` }))} />
                  : <p className="my-petit my-secondaire">Livraison à l’adresse de livraison par défaut de votre entreprise.</p>
              )}
              {mode === 'RETRAIT' && (
                creneaux.isPending ? <p>Chargement des créneaux…</p> : creneaux.isError ? <Alert ton="erreur">{messageErreur(creneaux.error)}</Alert> : (
                  <SelectField
                    label="Créneau de retrait"
                    value={creneau}
                    onChange={(e) => setCreneau(e.target.value)}
                    options={[{ value: '', label: 'Choisissez un créneau' }, ...(creneaux.data ?? []).map((c) => ({ value: `${c.creneauId}|${c.date}`, label: `${dateCourte(c.date)} · ${c.debut}–${c.fin} · ${c.placesRestantes ? `${c.placesRestantes} place(s)` : 'complet'}` }))]}
                  />
                )
              )}
              <p className="my-legende my-secondaire">Une commande est servie par un seul entrepôt (hypothèse DO-07).</p>
              <div><Button disabled={!remiseOk} onClick={() => setEtape('paiement')}>Continuer</Button></div>
            </div>
          </Card>
        )}

        {etape === 'paiement' && (
          <Card as="section" aria-labelledby="t-paiement">
            <div className="my-pile">
              <h2 id="t-paiement" className="my-titre-4">Moyen de paiement</h2>
              <fieldset className="my-pile my-pile--serre" style={{ border: 0, padding: 0, margin: 0 }}>
                <legend className="my-sr">Moyen de paiement</legend>
                {moyensCandidats.map((m, i) => {
                  const s = sondes[i]!;
                  const refuse = s.isError;
                  return (
                    <div key={m} className="my-pile my-pile--serre" style={{ gap: 2 }}>
                      <Choice type="radio" name="moyen" label={`${LIBELLE_MOYEN[m]}${s.isPending ? ' (vérification…)' : refuse ? ' — indisponible' : ''}`} checked={moyen === m} disabled={s.isPending || refuse} onChange={() => setMoyen(m)} />
                      {refuse && <span className="my-legende my-secondaire" style={{ paddingLeft: 32 }}>{messageErreur(s.error)}</span>}
                    </div>
                  );
                })}
              </fieldset>
              {moyen === 'VIREMENT' && <DecisionOuverte id="DO-02">Le stock n’est pas garanti tant que les fonds ne sont pas reçus. Votre commande expire si le virement n’est pas reçu à temps.</DecisionOuverte>}
              {moyen === 'CB_RETRAIT' && <DecisionOuverte id="DO-01">Paiement par carte au guichet : le stock n’est réservé qu’à l’encaissement. Variante simulée choisie dans le panneau de simulation.</DecisionOuverte>}
              {moyen === 'CB_EN_LIGNE' && <p className="my-petit my-secondaire">Vous serez redirigé vers la page de paiement sécurisée du prestataire. Aucune donnée de carte ne transite par MYRON.</p>}
              <div className="my-ligne">
                <Button variante="tertiaire" onClick={() => setEtape('remise')}>Retour</Button>
                <Button disabled={!moyen || !devis} onClick={() => setEtape('recap')}>Continuer</Button>
              </div>
            </div>
          </Card>
        )}

        {etape === 'recap' && devis && (
          <Card as="section" aria-labelledby="t-recap">
            <div className="my-pile">
              <h2 id="t-recap" className="my-titre-4">Récapitulatif</h2>
              <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0 }}>
                {devis.lignes.map((l) => (
                  <li key={`${l.produitId}-${l.conditionnementType}`} className="my-ligne my-ligne--espace"><span>{l.quantite} × {LIB_COND[l.conditionnementType!]} — {l.libelle ?? l.sku}</span><strong>{euros(l.totalHtCentimes)}</strong></li>
                ))}
              </ul>
              <hr className="my-separateur" />
              <dl className="my-dl">
                <dt>Total HT</dt><dd>{euros(devis.totalHtCentimes)}</dd>
                <dt>Frais de livraison HT</dt><dd>{devis.fraisLivraisonCentimes ? euros(devis.fraisLivraisonCentimes) : 'Offerts'}</dd>
                <dt>TVA</dt><dd>{euros(devis.totalTvaCentimes)}</dd>
                <dt>Total TTC</dt><dd className="my-prix" data-testid="total-ttc">{euros(devis.totalTtcCentimes)}</dd>
                <dt>Remise</dt><dd>{mode === 'LIVRAISON' ? 'Livraison' : `Retrait ${dateCourte(dateRetrait)}`} · {entrepots.data?.find((e) => e.id === entrepotId)?.nom}</dd>
                <dt>Paiement</dt><dd>{moyen && LIBELLE_MOYEN[moyen]}</dd>
                <dt>Prix garantis jusqu’au</dt><dd>{dateHeure(devis.expirationCommande)}</dd>
              </dl>
              {devis.avertissements?.includes('STOCK_NON_GARANTI_AVANT_PAIEMENT') && (
                <Alert ton="alerte" titre="Stock non garanti avant paiement">Le stock est réservé uniquement à la confirmation de votre paiement (règle MYRON).</Alert>
              )}
              {devis.avertissements?.includes('STOCK_FAIBLE') && <Alert ton="alerte">Stock faible dans cet entrepôt pour au moins un produit.</Alert>}
              {devis.avertissements?.includes('MINIMUM_NON_ATTEINT') && <Alert ton="erreur">Minimum de commande non atteint.</Alert>}
              {nouveauTotal !== null && <Alert ton="alerte" titre="Les prix ont changé">Nouveau total : <strong>{euros(nouveauTotal)}</strong> TTC. Vérifiez puis confirmez à nouveau.</Alert>}
              {!(erreur instanceof ApiErreur && erreur.code === 'PRIX_OBSOLETE') && <ErreurFormulaire erreur={erreur} />}
              <div className="my-ligne">
                <Button variante="tertiaire" onClick={() => setEtape('paiement')}>Retour</Button>
                <Button chargement={envoi} disabled={devisCourant?.isFetching || devis.avertissements?.includes('MINIMUM_NON_ATTEINT')} onClick={soumettre}>
                  {nouveauTotal !== null ? 'Confirmer avec le nouveau total' : moyen === 'CB_EN_LIGNE' ? 'Commander et payer' : 'Confirmer la commande'}
                </Button>
              </div>
              {moyen === 'CB_EN_LIGNE' && <p className="my-legende my-secondaire">Paiement par carte simulé. <MentionSimule /></p>}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
